/** Voucher types shown as Finance page tabs / forms. */

export const VOUCHER_TYPES = [
  {
    id: 'cash_payment',
    tabId: 'cpv',
    label: 'Cash Payment Voucher',
    shortLabel: 'Cash Payment',
    prefix: 'CPV',
    contraCode: 'CIH-CASH',
    contraTitle: 'Cash in Hand',
    mode: 'payment',
    bothSides: true,
  },
  {
    id: 'cash_receipt',
    tabId: 'crv',
    label: 'Cash Received Voucher',
    shortLabel: 'Cash Received',
    prefix: 'CRV',
    contraCode: 'CIH-CASH',
    contraTitle: 'Cash in Hand',
    mode: 'receipt',
    bothSides: true,
  },
  {
    id: 'bank_payment',
    tabId: 'bpv',
    label: 'Bank Payment Voucher',
    shortLabel: 'Bank Payment',
    prefix: 'BPV',
    contraCode: 'ORCV-BANK',
    contraTitle: 'Bank Receivables',
    mode: 'payment',
    requiresCheque: true,
    bothSides: true,
  },
  {
    id: 'bank_receipt',
    tabId: 'brv',
    label: 'Bank Received Voucher',
    shortLabel: 'Bank Received',
    prefix: 'BRV',
    contraCode: 'ORCV-BANK',
    contraTitle: 'Bank Receivables',
    mode: 'receipt',
    requiresCheque: true,
    bothSides: true,
  },
  {
    id: 'journal',
    tabId: 'jv',
    label: 'Journal Voucher',
    shortLabel: 'Journal Voucher',
    prefix: 'JV',
    contraCode: null,
    contraTitle: null,
    mode: 'journal',
  },
];

export function getVoucherType(idOrTab) {
  const key = String(idOrTab || '').trim().toLowerCase();
  return VOUCHER_TYPES.find(
    (v) => v.id === key || v.tabId === key,
  ) || null;
}

/** Local calendar YYYY-MM-DD (not UTC from toISOString). */
export function todayDate() {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Exact voucher / ledger calendar day — never shift via timezone parsing. */
export function calendarDateKey(value, fallback = '') {
  if (value == null || value === '') {
    return fallback ? calendarDateKey(fallback) : '';
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const year = value.getUTCFullYear();
    const month = String(value.getUTCMonth() + 1).padStart(2, '0');
    const day = String(value.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  const raw = String(value).trim();
  const iso = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (iso) return iso[1];
  const dmy = raw.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
  if (dmy) {
    return `${dmy[3]}-${dmy[2].padStart(2, '0')}-${dmy[1].padStart(2, '0')}`;
  }
  return fallback ? calendarDateKey(fallback) : '';
}

/** One editable row in the multi-entry voucher grid. */
export function emptyVoucherLine() {
  return {
    accountCode: '',
    accountTitle: '',
    description: '',
    debit: '',
    credit: '',
  };
}

const DEFAULT_GRID_ROWS = 10;

export function emptyVoucherForm(prefix = 'CPV', rowCount = DEFAULT_GRID_ROWS) {
  return {
    date: todayDate(),
    refNo: '',
    voucherNo: '',
    chequeNo: '',
    lines: Array.from({ length: Math.max(2, rowCount) }, () => emptyVoucherLine()),
  };
}

/** @deprecated use emptyVoucherForm */
export function emptySimpleVoucherForm(prefix = 'CPV') {
  return emptyVoucherForm(prefix, 10);
}

/** @deprecated use emptyVoucherLine */
export function emptyJournalLine() {
  return emptyVoucherLine();
}

/** @deprecated use emptyVoucherForm */
export function emptyJournalForm() {
  return emptyVoucherForm('JV', 10);
}

export function lineHasAmount(line) {
  return (Number(line?.debit) || 0) > 0 || (Number(line?.credit) || 0) > 0;
}

export function lineIsFilled(line) {
  return Boolean(String(line?.accountCode || '').trim()) && lineHasAmount(line);
}

export function sumLines(lines = []) {
  return (lines || []).reduce(
    (acc, line) => ({
      debit: acc.debit + (Number(line.debit) || 0),
      credit: acc.credit + (Number(line.credit) || 0),
    }),
    { debit: 0, credit: 0 },
  );
}

/** True when filled lines have matching debit and credit totals (> 0). */
export function linesAreBalanced(lines = []) {
  const filled = (lines || []).filter(lineIsFilled);
  if (!filled.length) return false;
  const { debit, credit } = sumLines(filled);
  return debit > 0 && Math.abs(debit - credit) < 0.01;
}

/** Map a saved voucher into the editable add/edit form shape. */
export function formFromVoucher(voucher, rowCount = DEFAULT_GRID_ROWS, { wholeAmounts = false } = {}) {
  const narration = String(voucher?.narration || '');
  const refMatch = narration.match(/^Ref:\s*([^·]+?)(?:\s*·|$)/i);
  const refNo = refMatch ? refMatch[1].trim() : '';
  const lineNarrationStrip = refNo
    ? narration.replace(new RegExp(`^Ref:\\s*${refNo.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*·?\\s*`, 'i'), '').trim()
    : narration;

  const amountText = (n) => {
    if (!(Number(n) > 0)) return '';
    return wholeAmounts ? String(Math.round(Number(n))) : String(n);
  };

  const lines = (voucher?.lines || []).map((line) => ({
    accountCode: String(line.accountCode || '').toUpperCase(),
    accountTitle: line.accountTitle || '',
    description: line.description || lineNarrationStrip || '',
    debit: amountText(line.debit),
    credit: amountText(line.credit),
  }));

  const minRows = Math.max(2, rowCount);
  while (lines.length < minRows) lines.push(emptyVoucherLine());
  // Keep one spare empty row for typing
  if (lines.length && lineIsFilled(lines[lines.length - 1])) {
    lines.push(emptyVoucherLine());
  }

  return {
    date: calendarDateKey(voucher?.date, todayDate()),
    refNo,
    voucherNo: String(voucher?.voucherNo || ''),
    chequeNo: String(voucher?.chequeNo || ''),
    lines,
  };
}
